/**
 * Application Service for Event Application System
 * 
 * This service handles all API calls related to the event application system
 */
import { logger } from '../utils/logger';
import { getApiBaseUrl } from '../utils/environmentConfig';
import { getFirestore, collection, getDocs, query, where, DocumentData } from 'firebase/firestore';
import { app } from '../firebase';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
/**
 * Upload an application photo to Firebase Storage and return the download URL
 * @param photo The photo file to upload
 * @param eventId The event ID for folder structure
 * @returns Promise with the download URL
 */
export const uploadApplicationPhoto = async (photo: File, eventId: string): Promise<string> => {
  logger.info('📸 Uploading application photo to Firebase Storage', { fileName: photo.name, eventId });

  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to upload application photos');
    }

    // Validate file type
    if (!photo.type.startsWith('image/')) {
      throw new Error('Invalid file type. Please upload an image file (JPEG, PNG, etc.)');
    }

    // Validate file size (max 5MB)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (photo.size > MAX_FILE_SIZE) {
      throw new Error('Image file is too large. Maximum allowed size is 5MB.');
    }

    // Create unique filename
    const fileName = `application-${Date.now()}-${photo.name.replace(/[^a-zA-Z0-9.]/g, '-')}`;
    const storage = getStorage();
    const photoRef = ref(storage, `applicationPhotos/${currentUser.uid}/${eventId}/${fileName}`);

    // Upload file
    await uploadBytes(photoRef, photo);
    logger.info('📸 Application photo uploaded successfully');

    // Get download URL
    const downloadURL = await getDownloadURL(photoRef);
    logger.success('✅ Application photo URL retrieved successfully');
    return downloadURL;
  } catch (error) {
    logger.error('❌ Error uploading application photo:', error);
    throw new Error(
      typeof error === 'object' && error && 'message' in error
        ? (error as Error).message
        : 'Failed to upload application photo'
    );
  }
};

// Firebase Cloud Function URLs
const SUBMIT_APPLICATION_ENDPOINT = `${getApiBaseUrl()}/submitEventApplication`;
const GET_APPLICATION_STATUS_ENDPOINT = `${getApiBaseUrl()}/getApplicationStatus`;
const ADMIN_UPDATE_STATUS_ENDPOINT = `${getApiBaseUrl()}/updateApplicationStatus`;

// Types
export type ApplicationSubmissionData = {
  name: string;
  email: string;
  consentAcknowledgment: boolean;
  dataRetentionAgreement: boolean;
  understandingConsent: string;
  introduction?: string; // User introduction for auto-filling event applications
  attendeeType: 'single' | 'couple';
  partnerAlias?: string;
  photoUrl?: string;
  eventId: string; // Required event UID from Firestore
};

export type ApplicationSubmissionResponse = {
  success: boolean;
  message: string;
  applicationId: string;
  trackingUrl: string;
};

export type ApplicationStatus = {
  id: string;
  status: 'pending' | 'rejected' | 'approved' | 'withdrawn';
  name: string;
  email: string;
  attendeeType: string;
  submittedAt: string;
  updatedAt: string;
  rejectionReason?: string;  eventDetails?: {
    date: string;
    time: string;
    address: string;  // Using only address for location consistency
    title?: string;
    description?: string;
    startTime?: {
      doorsOpen?: string;
      doorsClose?: string;
    };
    endTime?: string;
  };
  qrCodeUrl?: string;
  checkInStatus?: 'not_checked_in' | 'checked_in';
  checkInTime?: string;
};

// Admin application item interface
export interface ApplicationItem {
  id: string;
  name: string;
  email: string;
  userId: string;
  status: 'pending' | 'rejected' | 'approved' | 'withdrawn';
  submittedAt: string;
  updatedAt: string;
  attendeeType: string;
  partnerAlias?: string;
  photoUrl?: string;
  understandingConsent: string;
  rejectionReason?: string;
  qrCodeUrl?: string;
  checkInStatus?: 'not_checked_in' | 'checked_in';
  checkInTime?: string;
  eventId?: string; // ID of the event this application is for
}

/**
 * Submit an event application
 * @param formData The application form data
 * @param photo Optional photo file for upload
 * @returns Promise with the application submission response
 */

export const submitEventApplication = async (
  formData: ApplicationSubmissionData,
  photo?: File
): Promise<ApplicationSubmissionResponse> => {
  logger.info('📝 Submitting event application form', {
    emulator: process.env.REACT_APP_RUN_IN_EMULATOR === 'true',
    eventId: formData.eventId || 'none'
  });

  try {
    // Check authentication
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to submit an application');
    }

    // Force token refresh to ensure we have the latest authentication state
    await currentUser.getIdToken(true);
    
    // Get refreshed authentication token
    const token = await currentUser.getIdToken();
    
    // Log authentication details for debugging (without exposing sensitive token)
    logger.info('🔐 Authentication token prepared for submission', {
      userId: currentUser.uid,
      hasToken: !!token,
      tokenTruncated: token ? `${token.substring(0, 10)}...` : 'none'
    });

    // Handle photo upload if provided
    let photoUrl = formData.photoUrl || '';

    if (photo) {
      logger.info('📸 Processing photo for upload');
      try {
        photoUrl = await uploadApplicationPhoto(photo, formData.eventId);
        logger.success('✅ Photo uploaded and URL obtained');
      } catch (err) {
        logger.error('❌ Error uploading photo:', err);
        throw new Error('Failed to upload photo');
      }
    }

    // Create form data with photo URL
    const applicationData = {
      ...formData,
      photoUrl
    };

    logger.info('🚀 Sending application data to server', {
      endpoint: SUBMIT_APPLICATION_ENDPOINT,
      hasAuthHeader: !!token
    });

    const response = await fetch(SUBMIT_APPLICATION_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(applicationData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      logger.error('❌ Application submission failed:', errorData);
      throw new Error(errorData.error || 'Failed to submit application');
    }

    const data = await response.json();
    logger.success('✅ Application submitted successfully:', data);
    return data as ApplicationSubmissionResponse;
  } catch (error) {
    logger.error('❌ Error in submitEventApplication:', error);
    throw error;
  }
};

/**
 * Get the status of an application by ID
 * @param applicationId The application ID
 * @returns Promise with the application status
 */
export const getApplicationStatus = async (applicationId: string): Promise<ApplicationStatus> => {
  const requestUrl = `${GET_APPLICATION_STATUS_ENDPOINT}?id=${applicationId}`;
  const isEmulator = process.env.REACT_APP_RUN_IN_EMULATOR === 'true';
  
  logger.info('🔍 Starting application status fetch', { 
    applicationId, 
    endpoint: GET_APPLICATION_STATUS_ENDPOINT,
    requestUrl,
    isEmulator
  });
  
  try {
    // Check if user is logged in to add authentication header if available
    const auth = getAuth();
    const currentUser = auth.currentUser;
    let headers = {};
    
    // Add authentication token if user is logged in
    if (currentUser) {
      try {
        // Import auth utilities
        const { getFreshAuthToken } = await import('../utils/authUtils');
        
        // Get a fresh auth token
        const token = await getFreshAuthToken(
          currentUser, 
          true, 
          'application status fetch'
        );
        
        // Add auth header
        headers = {
          'Authorization': `Bearer ${token}`
        };
        
        logger.info('🔐 Added authentication to application status request');
      } catch (authError) {
        // Don't fail if authentication fails, just log it and continue without auth
        logger.warn('⚠️ Failed to add authentication to application status request, continuing without auth', {
          error: authError instanceof Error ? authError.message : 'Unknown error'
        });
      }
    }
    
    logger.info('🌐 Making fetch request', { 
      requestUrl,
      authenticated: Object.keys(headers).length > 0
    });
    
    const startTime = performance.now();
    const response = await fetch(requestUrl, { headers });
    const endTime = performance.now();
    const requestDuration = endTime - startTime;
    
    logger.info('📥 Received response from server', {
      status: response.status, 
      statusText: response.statusText,
      responseTime: `${Math.round(requestDuration)}ms`,
      ok: response.ok
    });
    
    if (!response.ok) {
      // Safe parsing of error response
      logger.warn('⚠️ Response not OK', {
        status: response.status,
        statusText: response.statusText
      });
      
      let errorData = { error: `HTTP error ${response.status}: ${response.statusText}` };
      let errorText = '';
      
      try {
        // Try to get text version first
        errorText = await response.text();
        logger.info('📄 Error response text:', { errorText, length: errorText.length });
        
        // Try to parse as JSON if it looks like JSON
        if (errorText && (errorText.startsWith('{') || errorText.startsWith('['))) {
          errorData = JSON.parse(errorText);
          logger.error('❌ Failed to fetch application status (parsed JSON):', errorData);
        } else {
          logger.error('❌ Failed to fetch application status (text response):', { errorText });
        }
      } catch (parseError) {
        logger.error('❌ Failed to parse error response:', parseError, { errorText });
      }
      
      throw new Error(errorData.error || `Failed to fetch application status (${response.status}): ${response.statusText}`);
    }
    
    let data;
    try {
      data = await response.json();
      logger.success('✅ Application status fetched successfully', { 
        id: data.id,
        status: data.status,
        dataFields: Object.keys(data),
        responseSize: JSON.stringify(data).length
      });
    } catch (jsonError) {
      logger.error('❌ Failed to parse successful response as JSON', jsonError);
      throw new Error('Invalid response format from server');
    }
    
    return data as ApplicationStatus;
  } catch (error) {
    logger.error('❌ Error in getApplicationStatus:', error, {
      applicationId,
      url: GET_APPLICATION_STATUS_ENDPOINT,
      isEmulator,
      timestamp: new Date().toISOString()
    });
    
    // Additional debugging for specific error types
    if (error instanceof TypeError) {
      logger.error('🔴 Network error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack?.split('\n').slice(0, 3).join('\n')
      });
    }
    
    throw error;
  }
};

/**
 * Get all applications for admin dashboard
 * @returns Promise with the list of applications
 */
/**
 * Get all applications for admin dashboard, optionally filtered by eventId
 * @param eventId Optional event ID to filter applications
 * @returns Promise with the list of applications
 */
export const getApplicationsList = async (eventId?: string): Promise<ApplicationItem[]> => {
  logger.info('🔍 Fetching applications list from Firestore', eventId ? { eventId } : {});
  try {
    const db = getFirestore(app);
    let q;
    if (eventId) {
      q = query(collection(db, 'applications'), where('eventId', '==', eventId));
    } else {
      q = collection(db, 'applications');
    }
    const snapshot = await getDocs(q);
    const applications: ApplicationItem[] = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        submittedAt: data.submittedAt?.toDate ? data.submittedAt.toDate().toISOString() : data.submittedAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : data.updatedAt,
        checkInTime: data.checkInTime?.toDate ? data.checkInTime.toDate().toISOString() : data.checkInTime,
      } as ApplicationItem;
    });
    logger.success('✅ Applications fetched from Firestore:', { count: applications.length });
    return applications;
  } catch (error) {
    logger.error('❌ Error in getApplicationsList (Firestore):', error);
    throw error;
  }
};

/**
 * Update the status of an application
 * @param applicationId The application ID
 * @param status The new status
 * @param rejectionReason Optional reason for rejection
 * @returns Promise with the updated application
 */
export const updateApplicationStatus = async (
  applicationId: string,
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn',
  rejectionReason?: string
): Promise<ApplicationItem> => {
  logger.info('🔄 Updating application status:', { applicationId, status });

  try {
    // Get current user token for authentication
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to update an application status');
    }

    // Force token refresh to ensure we have the latest claims
    await currentUser.getIdToken(true);
    
    // Import here to avoid circular dependencies
    const { checkAdminClaims } = await import('../utils/adminClaims');
    
    // Verify admin access
    const isAdmin = await checkAdminClaims(currentUser);
    if (!isAdmin) {
      logger.error('❌ Admin access required for updating application status');
      throw new Error('Admin access required');
    }

    const token = await currentUser.getIdToken();
    
    // Log authentication details for debugging
    logger.info('🔐 Admin authentication verified for status update', {
      userId: currentUser.uid,
      applicationId,
      hasToken: !!token,
      tokenTruncated: token ? `${token.substring(0, 10)}...` : 'none'
    });

    const response = await fetch(ADMIN_UPDATE_STATUS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        applicationId,
        status,
        rejectionReason
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      logger.error('❌ Failed to update application status:', errorData);
      throw new Error(errorData.message || 'Failed to update application status');
    }

    const data = await response.json();
    logger.success('✅ Application status updated successfully');
    return data as ApplicationItem;
  } catch (error) {
    logger.error('❌ Error in updateApplicationStatus:', error);
    throw error;
  }
};

/**
 * Withdraw or decline an application
 * @param applicationId The application ID
 * @param reason Optional reason for withdrawal/declining
 * @returns Promise with the updated application
 */
export const withdrawApplication = async (
  applicationId: string,
  reason?: string
): Promise<ApplicationItem> => {
  logger.info('🔄 Withdrawing application:', { applicationId });

  try {
    // Import auth utilities
    const { ensureAuthenticated, getFreshAuthToken } = await import('../utils/authUtils');
    
    // Get current user and ensure they're authenticated
    const auth = getAuth();
    const currentUser = auth.currentUser;
    ensureAuthenticated(currentUser, 'withdraw an application');
    
    // We can safely assert currentUser is not null due to ensureAuthenticated
    const token = await getFreshAuthToken(
      currentUser!, 
      true, 
      'application withdrawal'
    );

    const response = await fetch(ADMIN_UPDATE_STATUS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        applicationId,
        status: 'withdrawn',
        rejectionReason: reason || 'Application withdrawn by applicant'
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      logger.error('❌ Failed to withdraw application:', errorData);
      throw new Error(errorData.message || 'Failed to withdraw application');
    }

    const data = await response.json();
    logger.success('✅ Application withdrawn successfully');
    return data as ApplicationItem;
  } catch (error) {
    logger.error('❌ Error in withdrawApplication:', error);
    throw error;
  }
};

/**
 * Check if the current user has already applied to a specific event
 * @param eventId The event ID to check
 * @returns Object with hasApplied flag and applicationId if found
 */
export const checkUserEventApplication = async (eventId: string): Promise<{ hasApplied: boolean; applicationId?: string; status?: string }> => {
  // Check if the user is authenticated
  const auth = getAuth();
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    logger.info('🔍 No user authenticated, cannot check application status');
    return { hasApplied: false };
  }
  
  try {
    logger.info('🔍 Checking if user has already applied to event', { 
      eventId, 
      userId: currentUser.uid 
    });
    
    // Query Firestore to check if the user has an application for this event
    const db = getFirestore(app);
    const q = query(
      collection(db, 'applications'),
      where('eventId', '==', eventId),
      where('userId', '==', currentUser.uid)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      logger.info('📋 User has not applied to this event');
      return { hasApplied: false };
    }
    
    // User has an application for this event
    const doc = snapshot.docs[0];
    const data = doc.data() as DocumentData;
    
    // If application is withdrawn, consider it as not applied
    // so the user can apply again
    if (data.status === 'withdrawn') {
      logger.info('📋 User previously applied but withdrew application', {
        applicationId: doc.id,
        status: data.status
      });
      return { hasApplied: false };
    }
    
    logger.success('✅ Found existing active application for this event', { 
      applicationId: doc.id,
      status: data.status
    });
    
    return { 
      hasApplied: true, 
      applicationId: doc.id,
      status: data.status
    };
  } catch (error) {
    logger.error('❌ Error checking user application:', error);
    // In case of error, assume user hasn't applied to avoid blocking them
    return { hasApplied: false };
  }
};